/**
 * Validation helpers for the Generate Plan drawer's "Adjust bookings" flow.
 *
 * A slot edit is only allowed when the assigned resource can actually take the
 * work: the days must not already be booked out, and the hours must fit the
 * working-day capacity that remains in the slot's date range.
 *
 * Capacity is measured the same way the recommendation engine measures it —
 * `isBookingForResource` plus `consumesCapacity`, so cancelled and completed
 * bookings never reduce availability.
 */

import { Booking, Resource, WorkingHoursProfile } from '@/types';
import { consumesCapacity } from '@/lib/bookingCapacity';
import { isDayWithinContract } from '@/lib/contractWindow';
import { isBookingForResource } from '@/lib/recommendationEngine';
import { isWorkingHoursProfileConfigured } from '@/utils/heatmap-utils';
import {
  busyIntervalsForDay,
  dayIntervalsFromWindows,
  earliestFreeWindowFor,
  freeWindowsForDay,
  hhmmToMinutes,
  minutesToHHMM,
  overlapHours,
  overlapsBooking,
  DEFAULT_BUSINESS_HOURS_END,
  DEFAULT_BUSINESS_HOURS_START,
  type DayInterval,
  type TimeWindow,
} from '@/lib/dayAvailability';

/** Hours a resource can work on a single working day. */
export const WORKING_HOURS_PER_DAY = 8;

/** Fallback profile for companies with no working hours configured. */
export const DEFAULT_WORKING_PROFILE: WorkingHoursProfile = {
  monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8,
  saturday: 0, sunday: 0,
};

const PROFILE_DAY_KEYS: (keyof WorkingHoursProfile)[] = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

/**
 * A resource works its own profile when it has one, otherwise the company's.
 *
 * `customWorkingProfile` holds the resource's real daily hours whenever it is
 * populated — the resource sync writes it for every resource, and
 * `useCustomWorkingProfile` only governs whether org-default syncs may overwrite
 * it, not whether it is authoritative for scheduling. Gating on the flag made a
 * default-pattern resource whose real week is 8h/day be planned and validated
 * against the company's 7.5h, so the profile is honoured whenever configured —
 * as the backend's availability, and the heatmap, already do.
 */
export function effectiveWorkingProfile(
  resource: Pick<Resource, 'useCustomWorkingProfile' | 'customWorkingProfile'> | undefined | null,
  globalProfile?: WorkingHoursProfile,
): WorkingHoursProfile {
  const global = globalProfile ?? DEFAULT_WORKING_PROFILE;
  const own = resource?.customWorkingProfile;
  return isWorkingHoursProfileConfigured(own) ? own : global;
}

/** Contracted hours the profile gives the date's weekday. */
export function profileHoursForDate(date: Date, profile: WorkingHoursProfile): number {
  return profile[PROFILE_DAY_KEYS[date.getDay()]] ?? 0;
}

const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

export function dayKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function isWorkingDay(date: Date, profile?: WorkingHoursProfile): boolean {
  if (profile) return profileHoursForDate(date, profile) > 0;
  const weekday = date.getDay();
  return weekday !== 0 && weekday !== 6;
}

/** 'yyyy-MM-dd' parsed back to a local date, so weekdays read correctly. */
function dateFromDayKey(key: string): Date | null {
  const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!parsed) return null;
  return new Date(Number(parsed[1]), Number(parsed[2]) - 1, Number(parsed[3]));
}

/** Bookings that reduce the given resource's capacity. */
function capacityBookings(existingBookings: Booking[], resourceId: string): Booking[] {
  return existingBookings.filter(b => isBookingForResource(b, resourceId) && consumesCapacity(b));
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** 'yyyy-MM-dd' read from a date's UTC parts. */
function utcDayKey(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

function isUtcMidnight(date: Date): boolean {
  return date.getUTCHours() === 0 && date.getUTCMinutes() === 0
    && date.getUTCSeconds() === 0 && date.getUTCMilliseconds() === 0;
}

/** Every day in an inclusive range, as 'yyyy-MM-dd' keys. */
export function dayKeysInRange(startDate: Date, endDate: Date): string[] {
  const keys: string[] = [];
  let cursor = startOfDay(startDate);
  const last = startOfDay(endDate);
  while (cursor <= last) {
    keys.push(dayKey(cursor));
    cursor = new Date(cursor.getTime() + MS_PER_DAY);
  }
  return keys;
}

/**
 * Per-day booked hours as the backend reports them, from the plan availability
 * call's `dailyUtilisation`.
 */
export interface ReportedDayHours {
  /** 'yyyy-MM-dd', or any ISO string starting with one. */
  date: string;
  bookedHours: number;
}

/** The 'yyyy-MM-dd' a reported day names, whatever precision it carries. */
function reportedDayKey(date: string): string | null {
  const parsed = /^(\d{4}-\d{2}-\d{2})/.exec(date);
  return parsed ? parsed[1] : null;
}

/**
 * Hours already booked for the resource, keyed by 'yyyy-MM-dd'.
 *
 * `reportedDays` is the backend's own count for those days. It describes the
 * same bookings, so the two are not added up: each day takes whichever source
 * knows about more hours. In live mode the client's booking list is not
 * bulk-fetched, so the backend figure is usually the only one there is.
 */
export function bookedHoursByDay(
  existingBookings: Booking[],
  resourceId: string,
  reportedDays?: ReportedDayHours[],
): Map<string, number> {
  const byDay = new Map<string, number>();

  for (const booking of capacityBookings(existingBookings, resourceId)) {
    const start = new Date(booking.startTime);
    const end = new Date(booking.endTime);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) continue;

    // Whole-day bookings stored as UTC midnights carry no local time-of-day, so
    // their days are read in UTC terms: a local reading would shift them onto
    // the neighbouring day for any timezone with a non-zero offset.
    if (isUtcMidnight(start) && isUtcMidnight(end)) {
      for (let day = start.getTime(); day < end.getTime(); day += MS_PER_DAY) {
        const key = utcDayKey(new Date(day));
        byDay.set(key, (byDay.get(key) ?? 0) + 24);
      }
      continue;
    }

    let cursor = startOfDay(start);
    const lastDay = startOfDay(new Date(end.getTime() - 1));
    while (cursor <= lastDay) {
      const nextMidnight = new Date(cursor.getTime() + MS_PER_DAY);
      const overlapStart = Math.max(start.getTime(), cursor.getTime());
      const overlapEnd = Math.min(end.getTime(), nextMidnight.getTime());
      if (overlapEnd > overlapStart) {
        const key = dayKey(cursor);
        byDay.set(key, (byDay.get(key) ?? 0) + (overlapEnd - overlapStart) / MS_PER_HOUR);
      }
      cursor = nextMidnight;
    }
  }

  for (const day of reportedDays ?? []) {
    const key = reportedDayKey(day.date);
    if (!key || !(day.bookedHours > 0)) continue;
    byDay.set(key, Math.max(byDay.get(key) ?? 0, day.bookedHours));
  }

  return byDay;
}

/**
 * Days the resource cannot take another booking on: those whose booked hours
 * already fill the contracted day.
 *
 * A partially-booked day is not one of them — it can still take a booking for
 * the hours it has left, which `validateSlotEdit` checks per day. With a
 * working profile only days the profile gives hours to are considered: a day
 * with no contracted hours is a non-working day, which the caller already
 * treats as unselectable.
 */
export function unavailableDayKeys(
  existingBookings: Booking[],
  resourceId: string,
  workingProfile?: WorkingHoursProfile,
  reportedDays?: ReportedDayHours[],
): Set<string> {
  const unavailable = new Set<string>();
  for (const [key, hours] of bookedHoursByDay(existingBookings, resourceId, reportedDays)) {
    if (!workingProfile) {
      if (hours > 0) unavailable.add(key);
      continue;
    }
    const date = dateFromDayKey(key);
    const profileHours = date ? profileHoursForDate(date, workingProfile) : 0;
    if (profileHours > 0 && hours >= profileHours) unavailable.add(key);
  }
  return unavailable;
}

/**
 * A day a resource cannot work its full contracted hours on for a reason no
 * booking accounts for — a bank holiday or an absence.
 */
export interface UnavailableDay {
  /** 'yyyy-MM-dd' */
  date: string;
  /** Contracted hours the day loses. Omitted means all of them. */
  lostHours?: number;
  /** Why the day is unavailable, e.g. 'Christmas Day' or 'Annual leave'. */
  reason: string;
  /**
   * The parts of the day the lost hours fall in — the morning of a half-day AM
   * absence, an hourly absence's own span. Omitted when the hours are lost from
   * no particular part of the day, which leaves the day judged on its totals.
   */
  lostWindows?: TimeWindow[];
}

interface DayLoss {
  /** null means the whole day is lost. */
  lostHours: number | null;
  reason: string;
  /** When the lost hours fall, if that is known. */
  windows: DayInterval[] | null;
}

/**
 * The hours each day loses, keyed 'yyyy-MM-dd'. A day named more than once
 * keeps its largest loss, so a full-day absence is never reduced by a partial
 * one on the same day.
 */
function dayLossesByDay(days?: UnavailableDay[]): Map<string, DayLoss> {
  const byDay = new Map<string, DayLoss>();
  for (const day of days ?? []) {
    const key = day.date.slice(0, 10);
    const windows = day.lostWindows && day.lostWindows.length > 0
      ? dayIntervalsFromWindows(day.lostWindows)
      : null;
    const loss: DayLoss = {
      lostHours: day.lostHours ?? null,
      reason: day.reason,
      windows: windows && windows.length > 0 ? windows : null,
    };
    const known = byDay.get(key);
    const keepKnown = !!known && (
      known.lostHours === null
      || (loss.lostHours !== null && loss.lostHours <= known.lostHours)
    );
    if (!keepKnown) byDay.set(key, loss);
  }
  return byDay;
}

export interface RangeCapacity {
  /** Days in the inclusive range the resource works. */
  workingDays: number;
  /** Contracted hours across those days. */
  capacityHours: number;
  /** Hours already booked on those working days. */
  bookedHours: number;
  /** Hours still available for a new booking. */
  availableHours: number;
}

/**
 * Hours of a day already booked that fall inside the hours an absence takes.
 *
 * The two describe the same lost time — a booking made before the leave was
 * requested still sits in the morning the leave removes — so counting both in
 * full would take the day's capacity away twice.
 */
function bookedInsideLoss(
  existingBookings: Booking[],
  resourceId: string,
  date: Date,
  loss: DayLoss | undefined,
): number {
  if (!loss?.windows) return 0;
  return overlapHours(busyIntervalsForDay(existingBookings, resourceId, date), loss.windows);
}

/**
 * Working-day capacity left for a resource between two inclusive dates.
 * Booked hours are capped per day so an over-long booking cannot make the
 * available hours negative for the rest of the range.
 */
export function rangeCapacityForResource(
  existingBookings: Booking[],
  resourceId: string,
  startDate: Date,
  endDate: Date,
  workingHoursPerDay: number = WORKING_HOURS_PER_DAY,
  workingProfile?: WorkingHoursProfile,
  reportedDays?: ReportedDayHours[],
  unavailableDays?: UnavailableDay[],
): RangeCapacity {
  const booked = bookedHoursByDay(existingBookings, resourceId, reportedDays);
  const losses = dayLossesByDay(unavailableDays);

  let workingDays = 0;
  let bookedHours = 0;
  let capacityHours = 0;

  let cursor = startOfDay(startDate);
  const last = startOfDay(endDate);
  while (cursor <= last) {
    const contracted = workingProfile
      ? profileHoursForDate(cursor, workingProfile)
      : isWorkingDay(cursor) ? workingHoursPerDay : 0;
    // A bank holiday or absence takes its hours off the day before anything can
    // be booked into them.
    const loss = losses.get(dayKey(cursor));
    const dayCapacity = loss
      ? (loss.lostHours === null ? 0 : roundHours(Math.max(0, contracted - loss.lostHours)))
      : contracted;
    if (dayCapacity > 0) {
      workingDays++;
      capacityHours += dayCapacity;
      const bookedOnDay = booked.get(dayKey(cursor)) ?? 0;
      const insideLoss = bookedInsideLoss(existingBookings, resourceId, cursor, loss);
      bookedHours += Math.min(Math.max(0, bookedOnDay - insideLoss), dayCapacity);
    }
    cursor = new Date(cursor.getTime() + MS_PER_DAY);
  }

  return {
    workingDays,
    capacityHours,
    bookedHours: roundHours(bookedHours),
    availableHours: roundHours(Math.max(0, capacityHours - bookedHours)),
  };
}

function roundHours(hours: number): number {
  return Math.round(hours * 10) / 10;
}

/**
 * The window a plan's bookings may fall in: never before the request starts,
 * never after the last month the plan allocates to.
 */
export interface PlanDateBounds {
  /** Inclusive earliest day, 'yyyy-MM-dd' */
  minDate: string;
  /** Inclusive latest day, 'yyyy-MM-dd' */
  maxDate: string;
}

/** Last day of a 'yyyy-MM' month key. */
function endOfMonthKey(monthKey: string): string | null {
  const parsed = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (!parsed) return null;
  const year = Number(parsed[1]);
  const month = Number(parsed[2]);
  if (month < 1 || month > 12) return null;
  return dayKey(new Date(year, month, 0));
}

function toDayKey(value: string | Date | undefined | null): string | null {
  if (!value) return null;
  const date = toDate(value);
  return date ? dayKey(date) : null;
}

/**
 * The selectable window for a plan: from the request's start date to the end of
 * the last month the plan allocates hours to, falling back to the request's own
 * end date when the plan has no month keys.
 */
export function computePlanDateBounds(
  requestedStartDate: string | Date | undefined | null,
  monthKeys: string[],
  requestedEndDate?: string | Date | null,
): PlanDateBounds | null {
  const minDate = toDayKey(requestedStartDate);
  if (!minDate) return null;

  const monthEnds = monthKeys
    .map(endOfMonthKey)
    .filter((key): key is string => key !== null);

  const lastMonthEnd = monthEnds.length > 0
    ? monthEnds.reduce((latest, key) => (key > latest ? key : latest))
    : toDayKey(requestedEndDate);

  if (!lastMonthEnd) return null;
  return { minDate, maxDate: lastMonthEnd < minDate ? minDate : lastMonthEnd };
}

/** 'yyyy-MM' the date falls in. */
export function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** '31/08/2027' read from a 'yyyy-MM-dd' key, for user-facing text. */
export function formatDayKeyForDisplay(key: string): string {
  const parsed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!parsed) return key;
  return `${parsed[3]}/${parsed[2]}/${parsed[1]}`;
}

/** 'Jul 2026' read from a 'yyyy-MM' key. */
function monthLabel(key: string): string {
  const parsed = /^(\d{4})-(\d{2})$/.exec(key);
  if (!parsed) return key;
  return new Date(Number(parsed[1]), Number(parsed[2]) - 1)
    .toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

/** The parts of a slot its per-month hours are read from. */
export interface MonthlyHoursSlot {
  startDate: string | Date;
  endDate: string | Date;
  hours: number;
  dailyAllocations?: { date: string | Date; hours?: number }[];
}

/**
 * Hours a slot allocates per calendar month, keyed 'yyyy-MM'.
 *
 * A slot with a daily breakdown is read day by day. Without one it books its
 * whole range, so its hours are spread across the range's working days — that
 * way a range straddling a month boundary contributes to both months.
 */
export function monthlyHoursForSlot(
  slot: MonthlyHoursSlot,
  workingProfile?: WorkingHoursProfile,
): Record<string, number> {
  const byMonth: Record<string, number> = {};
  const add = (key: string, hours: number) => {
    byMonth[key] = roundHours((byMonth[key] ?? 0) + hours);
  };

  const daily = slot.dailyAllocations ?? [];
  if (daily.length > 0) {
    for (const day of daily) {
      const date = toDate(day.date);
      if (!date) continue;
      add(monthKey(date), day.hours ?? 0);
    }
    return byMonth;
  }

  const start = toDate(slot.startDate);
  const end = toDate(slot.endDate);
  if (!start || !end || end < start) return byMonth;

  const days: { key: string; weight: number }[] = [];
  let totalWeight = 0;
  let cursor = startOfDay(start);
  const last = startOfDay(end);
  while (cursor <= last) {
    const weight = workingProfile
      ? profileHoursForDate(cursor, workingProfile)
      : isWorkingDay(cursor) ? 1 : 0;
    days.push({ key: monthKey(cursor), weight });
    totalWeight += weight;
    cursor = new Date(cursor.getTime() + MS_PER_DAY);
  }

  // A range of non-working days only ever comes from a slot the planner could
  // not break down, so its hours land on the month it starts in.
  if (totalWeight <= 0) {
    add(monthKey(start), slot.hours);
    return byMonth;
  }

  for (const day of days) {
    if (day.weight <= 0) continue;
    add(day.key, (slot.hours * day.weight) / totalWeight);
  }
  return byMonth;
}

/**
 * The budget a plan's slots are judged against: what a month's budget allows
 * for the request's role, and what the rest of the plan already allocates to it.
 */
export interface MonthlyBudget {
  /** Budgeted hours for the request's role, keyed 'yyyy-MM'. */
  budgetedHoursByMonth: Record<string, number>;
  /**
   * Hours allocated to each month by everything other than the slot being
   * validated — the plan's other slots, plus anything already booked for the
   * request.
   */
  allocatedHoursByMonth?: Record<string, number>;
}

export type SlotValidationKind =
  | 'invalid-range'
  | 'invalid-time-window'
  | 'no-hours'
  | 'day-booked'
  | 'day-over-capacity'
  | 'no-free-window'
  | 'out-of-plan-range'
  | 'out-of-contract'
  | 'resource-conflict'
  | 'hours-exceed-capacity'
  | 'day-unavailable'
  | 'month-over-budget';

/**
 * How a validation result is treated. An `error` blocks the edit and the plan;
 * a `warning` is informational only — the user is told, and can still accept.
 */
export type SlotValidationSeverity = 'error' | 'warning';

export interface SlotValidationError {
  kind: SlotValidationKind;
  message: string;
  /** Absent means `'error'`: everything blocks unless it says otherwise. */
  severity?: SlotValidationSeverity;
}

/** A result blocks unless it is explicitly a warning. */
export function isBlockingValidation(
  result: SlotValidationError | null | undefined,
): result is SlotValidationError {
  return !!result && result.severity !== 'warning';
}

/** The blocking results of a `validatePlanSlots` pass, warnings dropped. */
export function blockingErrorsOnly(
  results: Map<string, SlotValidationError>,
): Map<string, SlotValidationError> {
  return new Map(
    Array.from(results).filter(([, result]) => isBlockingValidation(result)),
  );
}

/** The warning results of a `validatePlanSlots` pass, blocking errors dropped. */
export function warningsOnly(
  results: Map<string, SlotValidationError>,
): Map<string, SlotValidationError> {
  return new Map(
    Array.from(results).filter(([, result]) => result.severity === 'warning'),
  );
}

export interface SlotValidationInput {
  existingBookings: Booking[];
  resourceId: string;
  resourceName?: string;
  /** Inclusive slot range, 'yyyy-MM-dd' or any Date-parsable string. */
  startDate: string | Date;
  endDate: string | Date;
  /** Total hours the slot allocates across the range. */
  hours: number;
  /** Per-day dates the slot books, when it has a daily breakdown. */
  dayDates?: (string | Date)[];
  /** Per-day hours and time windows the slot books, when it has a daily breakdown. */
  dailyAllocations?: { date: string | Date; hours?: number; startTime?: string; endTime?: string }[];
  /** Availability from `computeRecommendations` for the merged date range. */
  availabilityStatus?: 'free' | 'partial' | 'conflict';
  /** Window the plan's bookings must stay inside */
  planDateBounds?: PlanDateBounds | null;
  /** The assigned resource's contract; a missing bound places no limit on that side. */
  contract?: Pick<Resource, 'contractStartDate' | 'contractEndDate'> | null;
  workingHoursPerDay?: number;
  /**
   * The resource's own per-weekday contracted hours. Preferred over
   * `workingHoursPerDay`, which only stands in when no profile is known.
   */
  workingProfile?: WorkingHoursProfile;
  /**
   * The role's monthly budget, when the project has one. Omitted when no budget
   * is known, which leaves the edit judged on capacity alone.
   */
  monthlyBudget?: MonthlyBudget | null;
  /**
   * The backend's per-day booked hours for this resource. The only source of
   * existing bookings in live mode, where the client holds none.
   */
  reportedBookedDays?: ReportedDayHours[];
  /**
   * Days the resource cannot work — bank holidays and absences. Bookings can
   * never be put on a day they take whole, and only take the hours they leave
   * on a day they take part of.
   */
  unavailableDays?: UnavailableDay[];
  /**
   * Days whose chosen time window is checked against the resource's existing
   * booking intervals, as 'yyyy-MM-dd' keys — the days the user has moved or
   * retimed. A suggested day is left to the hours-based checks: the planner
   * chose its window from the resource's own free gaps.
   */
  timeWindowCheckDays?: string[];
  /** The company's business hours, which bound a day's bookable window. */
  businessHoursStart?: number;
  businessHoursEnd?: number;
}

function toDate(value: string | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Validates an edited slot against the assigned resource's remaining capacity.
 * Returns null when the edit is allowed.
 *
 * A returned result is not always a block: budget overage comes back with
 * `severity: 'warning'`, which callers surface without gating on it. Use
 * `isBlockingValidation` to tell the two apart.
 */
export function validateSlotEdit(input: SlotValidationInput): SlotValidationError | null {
  const {
    existingBookings,
    resourceId,
    resourceName,
    hours,
    dailyAllocations,
    availabilityStatus,
    planDateBounds,
    contract,
    workingProfile,
    monthlyBudget,
    reportedBookedDays,
    unavailableDays,
    timeWindowCheckDays,
    workingHoursPerDay = WORKING_HOURS_PER_DAY,
    businessHoursStart = DEFAULT_BUSINESS_HOURS_START,
    businessHoursEnd = DEFAULT_BUSINESS_HOURS_END,
  } = input;

  /** Hours the resource is contracted to work on a given day. */
  const dayCapacityFor = (date: Date): number =>
    workingProfile ? profileHoursForDate(date, workingProfile) : workingHoursPerDay;

  const start = toDate(input.startDate);
  const end = toDate(input.endDate);
  const who = resourceName ?? 'This resource';
  const dayDates = input.dayDates ?? dailyAllocations?.map(d => d.date);

  if (!start || !end || end < start) {
    return { kind: 'invalid-range', message: 'End date must be on or after the start date' };
  }

  // Every booking sits inside a single day, so a day's end time must be later
  // than its start time — an end at or before the start is not an overnight
  // window, it is invalid.
  const invalidWindowDay = (dailyAllocations ?? []).find(day => {
    if (!day.startTime || !day.endTime) return false;
    const dayStart = toDate(day.startTime);
    const dayEnd = toDate(day.endTime);
    return !dayStart || !dayEnd || dayEnd <= dayStart;
  });
  if (invalidWindowDay) {
    const date = toDate(invalidWindowDay.date);
    return {
      kind: 'invalid-time-window',
      message: 'End time must be after the start time on '
        + `${date ? formatDayKeyForDisplay(dayKey(date)) : 'the edited day'}`,
    };
  }

  if (!(hours > 0)) {
    return { kind: 'no-hours', message: 'Booking must allocate at least some hours' };
  }

  if (planDateBounds) {
    const days = [start, end, ...(dayDates ?? []).map(toDate)]
      .filter((date): date is Date => date !== null)
      .map(dayKey);
    const outOfRange = days.find(
      day => day < planDateBounds.minDate || day > planDateBounds.maxDate,
    );
    if (outOfRange) {
      return {
        kind: 'out-of-plan-range',
        message: outOfRange < planDateBounds.minDate
          ? 'Bookings cannot start before the request start date '
            + `(${formatDayKeyForDisplay(planDateBounds.minDate)})`
          : 'Bookings cannot run past the plan\'s last month '
            + `(${formatDayKeyForDisplay(planDateBounds.maxDate)})`,
      };
    }
  }

  // The contract is one unbroken window, so a slot without per-day dates is
  // inside it when both ends of its range are.
  if (contract) {
    const contractDays = (dayDates && dayDates.length > 0 ? dayDates : [start, end])
      .map(toDate)
      .filter((date): date is Date => date !== null)
      .map(dayKey)
      .sort();
    const outsideDay = contractDays.find(day => !isDayWithinContract(contract, day));
    if (outsideDay) {
      const bound = contract.contractStartDate && outsideDay < contract.contractStartDate
        ? `starts on ${formatDayKeyForDisplay(contract.contractStartDate)}`
        : `ends on ${formatDayKeyForDisplay(contract.contractEndDate ?? outsideDay)}`;
      return {
        kind: 'out-of-contract',
        message: `${resourceName ?? 'This resource'}'s contract ${bound}, `
          + `so they cannot be booked on ${formatDayKeyForDisplay(outsideDay)}`,
      };
    }
  }

  const unavailable = unavailableDayKeys(
    existingBookings, resourceId, workingProfile, reportedBookedDays,
  );
  // The range's own ends are always booked days — allocation starts on the first
  // and finishes on the last — so they are checked even when the per-day dates
  // say otherwise, as they do while a date edit's breakdown is still the
  // suggestion's. Days in between stay flexible: allocation skips booked ones.
  const bookedDay = [...(dayDates ?? []), start, end]
    .map(toDate)
    .find(date => date !== null && unavailable.has(dayKey(date)));
  if (bookedDay) {
    return {
      kind: 'day-booked',
      message: `${who} is fully booked on ${formatDayKeyForDisplay(dayKey(bookedDay))}`,
    };
  }

  // A bank holiday or a full day's absence leaves the day with no hours at all,
  // which no booking on it accounts for: the day is checked in its own right so
  // moving a booking onto Christmas Day is refused rather than silently taken.
  const losses = dayLossesByDay(unavailableDays);
  for (const value of [...(dayDates ?? []), start, end]) {
    const date = toDate(value);
    if (!date) continue;
    const loss = losses.get(dayKey(date));
    if (!loss) continue;
    const contracted = dayCapacityFor(date);
    if (contracted <= 0) continue;
    if (loss.lostHours !== null && loss.lostHours < contracted) continue;
    return {
      kind: 'day-unavailable',
      message: `${who} is not available on ${formatDayKeyForDisplay(dayKey(date))}`
        + ` (${loss.reason})`,
    };
  }

  // An edited day's window has to clear the resource's bookings on it, which
  // the hours totals below cannot see: a day with 4h left has none of them free
  // at 09:00 when its existing booking runs 09:00–13:00.
  const timeWindowError = validateDayTimeWindows({
    existingBookings,
    resourceId,
    who,
    dailyAllocations,
    checkDays: timeWindowCheckDays,
    losses,
    workingProfile,
    businessHoursStart,
    businessHoursEnd,
  });
  if (timeWindowError) return timeWindowError;

  // A day can only take the hours the resource still has left on it: its
  // contracted hours for that weekday, less anything already booked.
  const bookedByDay = bookedHoursByDay(existingBookings, resourceId, reportedBookedDays);
  for (const day of dailyAllocations ?? []) {
    const allocated = roundHours(day.hours ?? 0);
    const date = toDate(day.date);
    const dayCapacity = date ? dayCapacityFor(date) : workingHoursPerDay;
    const alreadyBooked = date ? (bookedByDay.get(dayKey(date)) ?? 0) : 0;
    const loss = date ? losses.get(dayKey(date)) : undefined;
    const lost = loss ? (loss.lostHours ?? dayCapacity) : 0;
    // Hours booked inside the absence's own half of the day are already counted
    // as lost, so the day does not lose them a second time.
    const bookedTwice = date
      ? Math.min(bookedInsideLoss(existingBookings, resourceId, date, loss), alreadyBooked)
      : 0;
    const remaining = roundHours(
      Math.max(0, dayCapacity - alreadyBooked - lost + bookedTwice),
    );
    if (allocated <= remaining) continue;
    const where = date ? formatDayKeyForDisplay(dayKey(date)) : 'a day';
    if (loss) {
      return {
        kind: 'day-unavailable',
        message: `${allocated}h on ${where} exceeds the ${remaining}h ${who} has left`
          + ` that day (${loss.reason})`,
      };
    }
    return {
      kind: 'day-over-capacity',
      message: allocated > dayCapacity
        ? `${allocated}h on ${where} exceeds the ${roundHours(dayCapacity)}h working day`
        : `${allocated}h on ${where} exceeds the ${remaining}h ${who} has left that day`,
    };
  }

  const capacity = rangeCapacityForResource(
    existingBookings,
    resourceId,
    start,
    end,
    workingHoursPerDay,
    workingProfile,
    reportedBookedDays,
    unavailableDays,
  );

  if (availabilityStatus === 'conflict' || capacity.availableHours <= 0) {
    return {
      kind: 'resource-conflict',
      message: `${who} is fully booked between these dates`,
    };
  }

  if (roundHours(hours) > capacity.availableHours) {
    return {
      kind: 'hours-exceed-capacity',
      message: `${roundHours(hours)}h exceeds the ${capacity.availableHours}h ${who} has`
        + ' available between these dates',
    };
  }

  const budgetError = validateMonthlyBudget(
    { startDate: start, endDate: end, hours, dailyAllocations },
    monthlyBudget,
    workingProfile,
  );
  if (budgetError) return budgetError;

  return null;
}

/**
 * Checks the time window each edited day books against the resource's existing
 * bookings on it. A day can have the hours free and still have no room at the
 * chosen time, which the hours-based checks cannot see.
 */
function validateDayTimeWindows(input: {
  existingBookings: Booking[];
  resourceId: string;
  who: string;
  dailyAllocations?: { date: string | Date; hours?: number; startTime?: string; endTime?: string }[];
  checkDays?: string[];
  /** The hours each day loses to a holiday or an absence, keyed 'yyyy-MM-dd'. */
  losses: Map<string, DayLoss>;
  workingProfile?: WorkingHoursProfile;
  businessHoursStart: number;
  businessHoursEnd: number;
}): SlotValidationError | null {
  const {
    existingBookings, resourceId, who, dailyAllocations, checkDays, losses,
    workingProfile, businessHoursStart, businessHoursEnd,
  } = input;
  if (!checkDays || checkDays.length === 0) return null;
  const checked = new Set(checkDays);

  for (const day of dailyAllocations ?? []) {
    const date = toDate(day.date);
    if (!date || !checked.has(dayKey(date))) continue;
    if (!day.startTime || !day.endTime) continue;

    const startMinutes = hhmmToMinutes(timeOfDay(day.startTime));
    const endMinutes = hhmmToMinutes(timeOfDay(day.endTime));
    if (endMinutes <= startMinutes) continue;

    // The half day an absence takes is unbookable time the resource's bookings
    // say nothing about, so the window has to clear it as well.
    const loss = losses.get(dayKey(date));
    const absent = loss?.windows ?? [];
    const absentClash = overlapsBooking(absent, startMinutes, endMinutes);
    if (absentClash) {
      return {
        kind: 'day-unavailable',
        message: `${who} is not available ${minutesToHHMM(absentClash.startMinutes)}–`
          + `${minutesToHHMM(absentClash.endMinutes)} on `
          + `${formatDayKeyForDisplay(dayKey(date))} (${loss?.reason ?? 'Absent'})`,
      };
    }

    const busy = busyIntervalsForDay(existingBookings, resourceId, date);
    const clash = overlapsBooking(busy, startMinutes, endMinutes);
    if (!clash) continue;

    const requiredHours = roundHours(day.hours ?? (endMinutes - startMinutes) / 60);
    const freeWindows = freeWindowsForDay(
      existingBookings, resourceId, date, workingProfile,
      businessHoursStart, businessHoursEnd, absent,
    );
    if (earliestFreeWindowFor(freeWindows, requiredHours)) {
      return {
        kind: 'no-free-window',
        message: `${who} is already booked ${minutesToHHMM(clash.startMinutes)}–`
          + `${minutesToHHMM(clash.endMinutes)} on ${formatDayKeyForDisplay(dayKey(date))}`,
      };
    }
    return {
      kind: 'no-free-window',
      message: `${who} has no ${requiredHours}h free window on `
        + `${formatDayKeyForDisplay(dayKey(date))}`,
    };
  }

  return null;
}

/** The "HH:mm" a day's window boundary reads as, from "HH:mm" or a timestamp. */
function timeOfDay(value: string): string {
  const parsed = /[T ](\d{2}:\d{2})/.exec(value);
  return parsed ? parsed[1] : value;
}

/**
 * Checks a slot's hours against the role's budget for each month it allocates
 * to. A month the budget does not list has no hours budgeted for the role, so
 * moving a slot into it is over budget just as overfilling a listed month is.
 */
function validateMonthlyBudget(
  slot: MonthlyHoursSlot,
  monthlyBudget: MonthlyBudget | null | undefined,
  workingProfile?: WorkingHoursProfile,
): SlotValidationError | null {
  if (!monthlyBudget) return null;
  const { budgetedHoursByMonth, allocatedHoursByMonth } = monthlyBudget;
  if (Object.keys(budgetedHoursByMonth).length === 0) return null;

  const slotHoursByMonth = monthlyHoursForSlot(slot, workingProfile);

  for (const month of Object.keys(slotHoursByMonth).sort()) {
    const budgeted = roundHours(budgetedHoursByMonth[month] ?? 0);
    const allocated = roundHours(
      (allocatedHoursByMonth?.[month] ?? 0) + slotHoursByMonth[month],
    );
    if (allocated <= budgeted) continue;
    return {
      kind: 'month-over-budget',
      severity: 'warning',
      message: `${allocated}h allocated in ${monthLabel(month)} exceeds the `
        + `${budgeted}h budgeted for this role that month`,
    };
  }

  return null;
}

/** The parts of a proposed plan slot that validation reads. */
export interface ValidatablePlanSlot {
  id: string;
  resourceId: string | number;
  resourceName?: string;
  startDate: string;
  endDate: string;
  hours: number;
  /** The days the slot books, when the planner gave it a breakdown. */
  dailyBreakdown?: { date: string; hours?: number; startTime?: string; endTime?: string }[];
}

/** The parts of a slot edit that reach the allocation call. */
export interface ValidatableSlotEdit {
  resourceId?: string;
  resourceName?: string;
  hours?: number;
  startDate?: string;
  endDate?: string;
  /** The days the edit books, e.g. after a reassignment re-fitted them. */
  dailyBreakdown?: { date: string; hours?: number; startTime?: string; endTime?: string }[];
}

type ProfileResource = Pick<
  Resource, 'id' | 'name' | 'useCustomWorkingProfile' | 'customWorkingProfile'
> & Partial<Pick<Resource, 'contractStartDate' | 'contractEndDate'>>;

/** Day keys of an inclusive range given as strings, empty when unparsable. */
function dayKeysInRangeOf(startDate: string, endDate: string): string[] {
  const start = toDate(startDate);
  const end = toDate(endDate);
  if (!start || !end || end < start) return [];
  return dayKeysInRange(start, end);
}

/**
 * Validates every slot a plan would allocate — the suggested ones as much as the
 * adjusted ones, so a suggestion that lands on a day the resource is already
 * booked on is reported rather than silently allocated. Each slot is judged on
 * the resource, dates and hours that would actually be sent, against that
 * resource's own working profile.
 *
 * The map mixes blocking errors with warnings (budget overage); callers gate on
 * `blockingErrorsOnly` and surface `warningsOnly` for information.
 */
export function validatePlanSlots(input: {
  slots: ValidatablePlanSlot[];
  slotEdits?: Map<string, ValidatableSlotEdit>;
  existingBookings: Booking[];
  resources: ProfileResource[];
  globalWorkingProfile?: WorkingHoursProfile;
  /** Budgeted hours for the request's role, keyed 'yyyy-MM'. */
  budgetedHoursByMonth?: Record<string, number>;
  /** Hours already booked for the request, keyed 'yyyy-MM'. */
  bookedHoursByMonth?: Record<string, number>;
  /** The backend's per-day booked hours, keyed by resource id. */
  reportedBookedDaysByResourceId?: Record<string, ReportedDayHours[]>;
  /** Bank holidays and absences that take hours off a day, keyed by resource id. */
  unavailableDaysByResourceId?: Record<string, UnavailableDay[]>;
  planDateBounds?: PlanDateBounds | null;
}): Map<string, SlotValidationError> {
  const {
    slots, slotEdits, existingBookings, resources, globalWorkingProfile,
    budgetedHoursByMonth, bookedHoursByMonth,
    reportedBookedDaysByResourceId, unavailableDaysByResourceId, planDateBounds,
  } = input;
  const resourceById = new Map(resources.map(r => [String(r.id), r]));
  const errors = new Map<string, SlotValidationError>();

  const mergedSlots = slots.map(slot => {
    const edit = slotEdits?.get(slot.id);
    // The days the slot books. A breakdown names them, but an edited date range
    // outdates it, and a slot without one books its range whole. An edit that
    // brings its own breakdown — a reassignment re-fitted to another resource's
    // free days — names them itself, dates and all.
    const edited = edit?.dailyBreakdown;
    const breakdown = edited && edited.length > 0
      ? edited
      : edit?.startDate || edit?.endDate ? undefined : slot.dailyBreakdown;
    return {
      ...slot,
      startDate: edit?.startDate ?? slot.startDate,
      endDate: edit?.endDate ?? slot.endDate,
      hours: edit?.hours ?? slot.hours,
      breakdown,
    };
  });

  // Read a slot's months from its own per-day hours where it has them: spreading
  // its total across the range's working days instead rounds each day, so the
  // month totals drift from the bookings the slot actually creates.
  const monthlyHoursBySlotId = budgetedHoursByMonth
    ? new Map(mergedSlots.map(slot => [
      slot.id,
      monthlyHoursForSlot({
        startDate: slot.startDate,
        endDate: slot.endDate,
        hours: slot.hours,
        dailyAllocations: slot.breakdown,
      }),
    ]))
    : null;

  for (const slot of mergedSlots) {
    const edit = slotEdits?.get(slot.id);
    const resourceId = String(edit?.resourceId ?? slot.resourceId);
    const resource = resourceById.get(resourceId);
    // A slot without usable per-day hours books its range whole — either way the
    // days come from the range, so more than its two ends are checked.
    const { breakdown } = slot;
    const dayDates = breakdown && breakdown.length > 0
      ? breakdown.map(d => d.date)
      : dayKeysInRangeOf(slot.startDate, slot.endDate);
    const error = validateSlotEdit({
      existingBookings,
      resourceId,
      resourceName: edit?.resourceName ?? resource?.name ?? slot.resourceName,
      startDate: slot.startDate,
      endDate: slot.endDate,
      hours: slot.hours,
      dayDates,
      dailyAllocations: breakdown,
      planDateBounds,
      contract: resource,
      workingProfile: effectiveWorkingProfile(resource, globalWorkingProfile),
      monthlyBudget: budgetedHoursByMonth && monthlyHoursBySlotId
        ? {
          budgetedHoursByMonth,
          allocatedHoursByMonth: sumMonthlyHours([
            bookedHoursByMonth ?? {},
            ...Array.from(monthlyHoursBySlotId)
              .filter(([id]) => id !== slot.id)
              .map(([, hours]) => hours),
          ]),
        }
        : null,
      reportedBookedDays: reportedBookedDaysByResourceId?.[resourceId],
      unavailableDays: unavailableDaysByResourceId?.[resourceId],
    });
    if (error) errors.set(slot.id, error);
  }

  return errors;
}

/** Adds up per-month hour maps into one, keyed 'yyyy-MM'. */
export function sumMonthlyHours(maps: Record<string, number>[]): Record<string, number> {
  const total: Record<string, number> = {};
  for (const map of maps) {
    for (const [month, hours] of Object.entries(map)) {
      total[month] = roundHours((total[month] ?? 0) + hours);
    }
  }
  return total;
}
